// @flow

/* eslint-disable ta-code-conventions/has-matching-storydoc, ta-code-conventions/has-matching-test */

import * as React from 'react';
import { connect } from 'react-redux';
import { useMutation } from 'urql';

import decodeString from '@ta/common.decode';
import localStorage from '@ta/common.localstorage';
import Logging from '@ta/common.logging';
import BoostTrackingContext from '@ta/cpm.tracking';

import MixerImpressionContext from './MixerImpressionContext';
import FeedSectionIdContext from './FeedSectionIdContext';
import ItemIdContext from './ItemIdContext';
import TrackingNamespaceContext from './TrackingNamespaceContext';
import HomeTrackingContext from './HomeTrackingContext';
import ProfileTrackingContext from './ProfileTrackingContext';
import SearchTrackingContext from './SearchTrackingContext';
import UgcDetailTrackingContext from './UgcDetailTrackingContext';
import RecommendedFolloweesImpressionIdContext from './RecommendedFolloweesImpressionIdContext';
import OnboardingTrackingContext from './OnboardingTrackingContext';
import SocialHotelsTrackingContext from './SocialHotelsTrackingContext';
import ListPageTrackingContext from './ListPageTrackingContext';
import RestaurantsTrackingContext from './RestaurantsTrackingContext';
import InboxTrackingContext from './InboxTrackingContext';
import LogInteractions, { type LogInteractionsVariables } from './LogInteractions.gql';
import GlobalNavTrackingContext from './GlobalNavTrackingContext';
import ContentUploaderTrackingContext from './ContentUploaderTrackingContext';
import FlightsShelfTrackingContext from './FlightsShelfTrackingContext';
import FlightsSearchFormContext from './FlightsSearchFormContext';
import FlightsSearchResultsContext from './FlightsSearchResultsContext';
import ItineraryContext from './ItineraryContext';
import SavingsTipsContext from './SavingsTipsContext';
import CrossSiteClickContext from './CrossSiteClickContext';
import FlightsGoogleOneTapClickContext from './FlightsGoogleOneTapClickContext';

/** InteractionTypes are defined in the Interaction microservice within each namespace */
export type InteractionType = string;
/** The log interaction function */
export type LogInteraction = (
  event: ?SyntheticEvent<*>,
  interactionType: InteractionType,
  // TODO: ML-4165 There are A LOT of incorrect usages of tracking. Remove the any type once all of them are fixed
  variables?: LogInteractionsVariables | any,
) => mixed;

type LogInteractionMutation = (variables: LogInteractionsVariables) => Promise<mixed>;

type ExternalProps = {|
  /** The required child function */
  children: LogInteraction => React.Node,
|};

type Props = {|
  ...ExternalProps,
  loggedInUserId: ?string, // From Redux
  isReadonlyMode: boolean, // From Redux
|};

const interactionDataKey = 'interactionData';

const logDelayedInteractions = loggingFunction => {
  const batchedData = localStorage.get(interactionDataKey);
  if (batchedData) {
    loggingFunction({
      interactions: batchedData,
      clientRequestTimestamp: new Date().getTime(),
    });
    localStorage.remove(interactionDataKey);
  }
};

const storeInteraction = data => {
  let batchedData = localStorage.get(interactionDataKey);
  if (!batchedData) {
    batchedData = [];
  }
  const interactionWithTimestamp = {
    eventTimestampMsClient: new Date().getTime(),
    interaction: data,
  };
  batchedData.push(interactionWithTimestamp);
  localStorage.set(interactionDataKey, batchedData);
};

// When navigator.sendBeacon works properly on all browsers we can replace this
const eventIsLink = event => {
  if (!event) {
    return false;
  }
  const { target, currentTarget } = event;
  if (target && currentTarget && target instanceof HTMLElement) {
    let currentNode = (target: HTMLElement);
    let aboveTarget = false;
    do {
      if (currentNode instanceof HTMLElement && currentNode.dataset.encodedUrl) {
        // convert element w/ data-encodedUrl to anchor tag, then let
        // the next block handle it.
        const decodedUrl = decodeString(currentNode.dataset.encodedUrl);
        // eslint-disable-next-line no-restricted-globals
        const newAnchor = document.createElement('a');
        newAnchor.href = decodedUrl;
        if (currentNode.parentElement instanceof HTMLElement) {
          newAnchor.style.display = 'none';
          currentNode.parentElement.appendChild(newAnchor);
        }
        currentNode = newAnchor;
      }

      if (currentNode instanceof HTMLAnchorElement) {
        const anchor = (currentNode: HTMLAnchorElement);
        /* eslint-disable no-restricted-globals */
        if (
          window &&
          window.location &&
          window.location.hostname &&
          window.location.hostname === anchor.hostname
        ) {
          return true;
        }
      }
      aboveTarget = currentNode === currentTarget || aboveTarget;
      currentNode = currentNode.parentElement;
    } while (
      // end once we reach the parent of this component where the event handler is attached
      currentNode &&
      !aboveTarget
    );
  }
  return false;
};

const logDataLaterOrNow = (event, data, interactionMutationFunction) => {
  storeInteraction(data);
  if (!SERVER && !eventIsLink(event)) {
    logDelayedInteractions(interactionMutationFunction);
  }
};

const trackBoost = (
  interactionMutationFunction: LogInteractionMutation,
  props: Props,
  itemType: string,
  itemId: string,
) => {
  const { children } = props;
  const logOrDelayInteraction = (
    event: ?SyntheticEvent<*>,
    interactionType: InteractionType,
    additionalVariables?: {},
  ) => {
    const variables = additionalVariables || {};
    const boostData = {};
    switch (interactionType) {
      case 'overflowMenuClick':
      case 'linkClicked':
        boostData[interactionType] = {
          itemType,
          itemId,
        };
        break;
      case 'linkedPoiClick':
        boostData[interactionType] = {
          itemType,
          itemId,
          placement: 'HEADER',
        };
        Object.assign(boostData[interactionType], variables);
        break;
      case 'follow':
      case 'unfollow':
      case 'userClick':
      case 'save':
        boostData[interactionType] = {
          itemType,
          itemId,
        };
        Object.assign(boostData[interactionType], variables);
        break;
      default: {
        const boostTypeString = interactionType || 'undefined';
        Logging.error(`Unknown boost interaction type: ${boostTypeString}`);
        boostData[interactionType] = {
          itemType,
          itemId,
        };
        Object.assign(boostData[interactionType], variables);
      }
    }
    const interactionData = {
      boost: boostData,
    };
    logDataLaterOrNow(event, interactionData, interactionMutationFunction);
  };
  return children(logOrDelayInteraction);
};

const trackMixer = (interactionMutationFunction: LogInteractionMutation, props: Props) => {
  const { children } = props;
  return (
    <MixerImpressionContext.Consumer>
      {mixerImpressionId => (
        <FeedSectionIdContext.Consumer>
          {feedSectionId => (
            <ItemIdContext.Consumer>
              {itemId => {
                const logOrDelayInteraction = (
                  event: ?SyntheticEvent<*>,
                  interactionType: InteractionType,
                  additionalVariables?: {},
                ) => {
                  const variables = additionalVariables || {};
                  const mixerData = {};
                  switch (interactionType) {
                    case 'delete':
                    case 'linkedPoiScroll':
                    case 'overflowMenuClick':
                    case 'report':
                    case 'seeAll':
                    case 'shelfScroll':
                    case 'untag':
                    case 'untagConfirm':
                    case 'untagCancel':
                      mixerData[interactionType] = {
                        feedSectionId,
                      };
                      break;
                    case 'linkedPhotoClick':
                      mixerData[interactionType] = {
                        feedSectionId,
                      };
                      Object.assign(mixerData[interactionType], variables);
                      break;
                    case 'linkedPoiClick':
                      mixerData[interactionType] = {
                        feedSectionId,
                        placement: 'HEADER',
                      };
                      Object.assign(mixerData[interactionType], variables);
                      break;
                    case 'feedClick':
                    case 'helpful':
                    case 'repost':
                    case 'share':
                    case 'unhelpful':
                    case 'follow':
                    case 'unfollow':
                    case 'userClick':
                    case 'save':
                    case 'trip_save':
                    case 'dismiss':
                    case 'userReferenceClick':
                    case 'linkReferenceClick':
                    case 'linkedSnippetClick':
                    case 'batchGalleryClick':
                    case 'batchGalleryScroll':
                      mixerData[interactionType] = {
                        feedSectionId,
                        itemId: itemId.itemId,
                      };
                      Object.assign(mixerData[interactionType], variables);
                      break;
                    case 'showMore':
                      mixerData[interactionType] = {
                        mixerImpressionId,
                      };
                      Object.assign(mixerData[interactionType], variables);
                      break;
                    default: {
                      const mixerTypeString = interactionType || 'undefined';
                      Logging.error(`Unknown mixer interaction type: ${mixerTypeString}`);
                    }
                  }
                  const interactionData = {
                    mixer: mixerData,
                  };
                  logDataLaterOrNow(event, interactionData, interactionMutationFunction);
                };
                return children(logOrDelayInteraction);
              }}
            </ItemIdContext.Consumer>
          )}
        </FeedSectionIdContext.Consumer>
      )}
    </MixerImpressionContext.Consumer>
  );
};

const trackHome = (interactionMutationFunction: LogInteractionMutation, props: Props) => {
  const { children } = props;
  return (
    <HomeTrackingContext.Consumer>
      {homeContext => (
        <RecommendedFolloweesImpressionIdContext.Consumer>
          {impressionIdContext => {
            const logOrDelayInteraction = (
              event: ?SyntheticEvent<*>,
              interactionType: InteractionType,
              additionalVariables?: {},
            ) => {
              const variables = additionalVariables || {};
              const homeData = {};
              const graphqlInteractionType =
                interactionType === 'unfollow' ? 'unFollow' : interactionType;
              homeData[graphqlInteractionType] = {
                ...homeContext,
                ...variables,
                ...impressionIdContext,
              };

              const interactionData = {
                home: homeData,
              };
              logDataLaterOrNow(event, interactionData, interactionMutationFunction);
            };
            return children(logOrDelayInteraction);
          }}
        </RecommendedFolloweesImpressionIdContext.Consumer>
      )}
    </HomeTrackingContext.Consumer>
  );
};

const trackProfile = (interactionMutationFunction: LogInteractionMutation, props: Props) => {
  const { children } = props;
  return (
    <ProfileTrackingContext.Consumer>
      {profileContext => (
        <RecommendedFolloweesImpressionIdContext.Consumer>
          {impressionIdContext => {
            const logOrDelayInteraction = (
              event: ?SyntheticEvent<*>,
              interactionType: InteractionType,
              additionalVariables?: {},
            ) => {
              const variables = additionalVariables || {};
              const profileData = {};
              const graphqlInteractionType =
                interactionType === 'unfollow' ? 'unFollow' : interactionType;
              profileData[graphqlInteractionType] = {
                ...profileContext,
                ...variables,
                ...impressionIdContext,
              };

              const interactionData = {
                profile: profileData,
              };
              logDataLaterOrNow(event, interactionData, interactionMutationFunction);
            };
            return children(logOrDelayInteraction);
          }}
        </RecommendedFolloweesImpressionIdContext.Consumer>
      )}
    </ProfileTrackingContext.Consumer>
  );
};

const trackUgcDetail = (interactionMutationFunction: LogInteractionMutation, props: Props) => {
  const { children, loggedInUserId } = props;
  return (
    <UgcDetailTrackingContext.Consumer>
      {ugcDetailContext => {
        const logOrDelayInteraction = (
          event: ?SyntheticEvent<*>,
          interactionType: InteractionType,
          additionalVariables?: {},
        ) => {
          const variables = additionalVariables || {};
          const actorUserIdData = loggedInUserId ? { actorUserId: loggedInUserId } : {};
          const ugcDetailData = {};
          const graphqlInteractionType =
            interactionType === 'feedClick' ? 'ugcClick' : interactionType;
          ugcDetailData[graphqlInteractionType] = {
            ...ugcDetailContext,
            ...variables,
            ...actorUserIdData,
          };

          const interactionData = {
            ugcDetail: ugcDetailData,
          };
          logDataLaterOrNow(event, interactionData, interactionMutationFunction);
        };
        return children(logOrDelayInteraction);
      }}
    </UgcDetailTrackingContext.Consumer>
  );
};

const trackOnboarding = (interactionMutationFunction: LogInteractionMutation, props: Props) => {
  const { children } = props;
  return (
    <OnboardingTrackingContext.Consumer>
      {onboardingContext => {
        const logOrDelayInteraction = (
          event: ?SyntheticEvent<*>,
          interactionType: InteractionType,
          additionalVariables?: {},
        ) => {
          const variables = additionalVariables || {};
          const onboardingData = {};
          onboardingData[interactionType] = {
            ...variables,
            ...onboardingContext,
          };

          const interactionData = {
            onboarding: onboardingData,
          };
          logDataLaterOrNow(event, interactionData, interactionMutationFunction);
        };
        return children(logOrDelayInteraction);
      }}
    </OnboardingTrackingContext.Consumer>
  );
};

const trackInbox = (interactionMutationFunction: LogInteractionMutation, props: Props) => {
  const { children } = props;
  return (
    <InboxTrackingContext.Consumer>
      {inboxContext => {
        const logOrDelayInteraction = (
          event: ?SyntheticEvent<*>,
          interactionType: InteractionType,
          additionalVariables?: {},
        ) => {
          const variables = additionalVariables || {};
          const inboxData = {};
          switch (interactionType) {
            case 'follow':
            case 'unfollow':
              inboxData[interactionType] = {};
              Object.assign(inboxData[interactionType], variables);
              Object.assign(inboxData[interactionType], inboxContext);
              break;
            default: {
              const inboxTypeString = interactionType || 'undefined';
              Logging.error(`Unknown or not set up inbox interactionType: ${inboxTypeString}`);
            }
          }
          const interactionData = {
            inbox: inboxData,
          };
          logDataLaterOrNow(event, interactionData, interactionMutationFunction);
        };
        return children(logOrDelayInteraction);
      }}
    </InboxTrackingContext.Consumer>
  );
};

const trackSearch = (interactionMutationFunction: LogInteractionMutation, props: Props) => {
  const { children } = props;
  return (
    <SearchTrackingContext.Consumer>
      {searchContext => {
        const logOrDelayInteraction = (
          event: ?SyntheticEvent<*>,
          interactionType: InteractionType,
          additionalVariables?: {},
        ) => {
          const variables = additionalVariables || {};
          const searchData = {};
          switch (interactionType) {
            case 'follow':
            case 'unfollow':
              searchData[interactionType] = {};
              Object.assign(searchData[interactionType], variables);
              Object.assign(searchData[interactionType], searchContext);
              break;
            default: {
              const searchTypeString = interactionType || 'undefined';
              Logging.error(`Unknown or not set up search interactionType: ${searchTypeString}`);
            }
          }
          const interactionData = {
            search: searchData,
          };
          logDataLaterOrNow(event, interactionData, interactionMutationFunction);
        };
        return children(logOrDelayInteraction);
      }}
    </SearchTrackingContext.Consumer>
  );
};

const trackHotels = (
  interactionMutationFunction: LogInteractionMutation,
  props: Props,
  namespace: string,
) => {
  const { children } = props;
  return (
    <SocialHotelsTrackingContext.Consumer>
      {socailHotelsTrackingContext => {
        const logOrDelayInteraction = (
          event: ?SyntheticEvent<*>,
          interactionType: InteractionType,
          additionalVariables?: {},
        ) => {
          const variables = additionalVariables || {};
          const hotelsData = {};
          hotelsData[interactionType] = {
            ...variables,
            ...socailHotelsTrackingContext,
          };
          const namespaceData = {};
          namespaceData[namespace] = hotelsData;
          logDataLaterOrNow(event, namespaceData, interactionMutationFunction);
        };
        return children(logOrDelayInteraction);
      }}
    </SocialHotelsTrackingContext.Consumer>
  );
};

const TrackHotelsListPage = ({
  interactionMutationFunction,
  props,
  namespace,
}: {
  interactionMutationFunction: LogInteractionMutation,
  props: Props,
  namespace: string,
}) => {
  const { children } = props;
  const impressionKey = React.useContext(ListPageTrackingContext);
  const previousImpressionKey = React.useRef(impressionKey);
  const logQueue = React.useRef([]);

  // Queue up requests to log data in order to decouple logging callback function from impressionKey
  const addToQueue = React.useCallback(
    (event: ?SyntheticEvent<*>, interactionType: InteractionType, additionalVariables?: {}) => {
      logQueue.current.push({ event, interactionType, additionalVariables });
    },
    [],
  );

  // Do the actual mutation
  const logWithImpressionKey = React.useCallback(
    (
      event: ?SyntheticEvent<*>,
      interactionType: InteractionType,
      additionalVariables?: {},
      impressionKeyToLog: ?string,
    ) => {
      const variables = additionalVariables || {};
      const hotelsData = {};
      hotelsData[interactionType] = {
        ...variables,
        impressionKey: impressionKeyToLog,
      };
      const namespaceData = {};
      namespaceData[namespace] = hotelsData;
      logDataLaterOrNow(event, namespaceData, interactionMutationFunction);
    },
    [interactionMutationFunction, namespace],
  );

  // Trigger mutations for queued log requests when the impression key changes
  React.useEffect(() => {
    if (logQueue.current.length > 0 && previousImpressionKey.current !== impressionKey) {
      const impressionKeyToLog = previousImpressionKey.current;
      logQueue.current.forEach(({ event, interactionType, additionalVariables }) => {
        logWithImpressionKey(event, interactionType, additionalVariables, impressionKeyToLog);
      });
      logQueue.current = [];
      previousImpressionKey.current = impressionKey;
    }
  }, [impressionKey, logWithImpressionKey]);

  return children(addToQueue);
};

const trackFlightsResults = (
  interactionMutationFunction: LogInteractionMutation,
  props: Props,
  namespace: string,
) => {
  const { children } = props;
  return (
    <FlightsSearchResultsContext.Consumer>
      {SearchResultsContextData => (
        <ItineraryContext.Consumer>
          {ItineraryContextData => (
            <SavingsTipsContext.Consumer>
              {SavingsTipsContextData => {
                const logOrDelayInteraction = (
                  event: ?SyntheticEvent<*>,
                  interactionType: InteractionType,
                  additionalVariables?: {},
                ) => {
                  const variables = additionalVariables || {};
                  const flightsData = {};

                  switch (interactionType) {
                    case 'filter':
                      flightsData[interactionType] = {
                        ...variables,
                        ...SearchResultsContextData,
                      };
                      break;
                    case 'clearAllFilters':
                    case 'sortDropdown':
                    case 'removeFilter':
                    case 'newSortResults':
                    case 'filterHamburger':
                    case 'sort':
                      flightsData[interactionType] = {
                        ...variables,
                        ...SearchResultsContextData,
                      };
                      break;
                    case 'staticPhotoClick':
                      flightsData[interactionType] = {
                        ...variables,
                        searchId: SearchResultsContextData?.searchId || '',
                      };
                      break;
                    case 'firstClick':
                    case 'viewDealClick':
                    case 'viewDealPersistClick':
                    case 'viewDealFATableClick':
                    case 'viewDealRightRail':
                    case 'secondClickBreadCrumb':
                    case 'secondClickTabDetails':
                    case 'secondClickTabReviews':
                    case 'seatMapClick':
                    case 'airlineReviewExpand':
                    case 'airlineReviewClick':
                    case 'viewDealsExpand':
                    case 'amenitiesHover':
                    case 'segmentFilter':
                    case 'flyScoreHover':
                    case 'brandedFareHover':
                    case 'selfTransferHover':
                    case 'comboFareHover':
                      flightsData[interactionType] = {
                        ...variables,
                        ...ItineraryContextData,
                        ...SearchResultsContextData,
                      };
                      break;
                    case 'savingsTipClick':
                    case 'savingsTipExpand':
                      flightsData[interactionType] = {
                        ...variables,
                        ...SavingsTipsContextData,
                        searchId: SearchResultsContextData?.searchId || '',
                      };
                      break;

                    default: // all other interaction types use ItineraryInteraction
                    {
                      const searchTypeString = interactionType || 'undefined';
                      Logging.error(
                        `Unknown or not set up search interactionType: ${searchTypeString}`,
                      );
                    }
                  }
                  const namespaceData = {};
                  namespaceData[namespace] = flightsData;
                  logDataLaterOrNow(event, namespaceData, interactionMutationFunction);
                };
                return children(logOrDelayInteraction);
              }}
            </SavingsTipsContext.Consumer>
          )}
        </ItineraryContext.Consumer>
      )}
    </FlightsSearchResultsContext.Consumer>
  );
};

const trackFlights = (
  interactionMutationFunction: LogInteractionMutation,
  props: Props,
  namespace: string,
) => {
  const { children } = props;
  return (
    <CrossSiteClickContext.Consumer>
      {CrossSiteClickContextData => (
        <ItineraryContext.Consumer>
          {ItineraryContextData => (
            <FlightsShelfTrackingContext.Consumer>
              {FlightsShelfTrackingContextData => (
                <FlightsSearchFormContext.Consumer>
                  {FlightsSearchFormContextData => (
                    <FlightsGoogleOneTapClickContext.Consumer>
                      {GoogleOneTapContextData => {
                        const logOrDelayInteraction = (
                          event: ?SyntheticEvent<*>,
                          interactionType: InteractionType,
                          additionalVariables?: {},
                        ) => {
                          const variables = additionalVariables || {};
                          const flightsData = {};
                          switch (interactionType) {
                            case 'fareCardClick':
                              flightsData[interactionType] = {
                                ...variables,
                                ...ItineraryContextData,
                              };
                              break;

                            case 'crossSiteClick':
                              flightsData[interactionType] = {
                                ...variables,
                                ...CrossSiteClickContextData,
                              };
                              break;

                            case 'googleOneTapClick':
                              flightsData[interactionType] = {
                                ...variables,
                                ...GoogleOneTapContextData,
                              };
                              break;

                            case 'cosChange':
                            case 'passengerNumberChange':
                            case 'childAgeChange':
                            case 'airportTypeaheadSelection':
                            case 'itineraryTypeTabChange':
                            case 'datepickerInteraction':
                            case 'checkNonstop':
                            case 'checkNearby':
                            case 'checkSubscription':
                            case 'stickyShown':
                            case 'findFlightsClick':
                              flightsData[interactionType] = {
                                ...variables,
                                ...FlightsSearchFormContextData,
                              };
                              break;

                            case 'attractionsShelfSeeAllClick':
                            case 'attractionsShelfItemClick':
                            case 'attractionsShelfScroll':
                              flightsData[interactionType] = {
                                ...FlightsShelfTrackingContextData,
                                ...variables,
                              };
                              break;

                            default: {
                              const searchTypeString = interactionType || 'undefined';
                              Logging.error(
                                `Unknown or not set up search interactionType: ${searchTypeString}`,
                              );
                            }
                          }
                          const namespaceData = {};
                          namespaceData[namespace] = flightsData;
                          logDataLaterOrNow(event, namespaceData, interactionMutationFunction);
                        };

                        return children(logOrDelayInteraction);
                      }}
                    </FlightsGoogleOneTapClickContext.Consumer>
                  )}
                </FlightsSearchFormContext.Consumer>
              )}
            </FlightsShelfTrackingContext.Consumer>
          )}
        </ItineraryContext.Consumer>
      )}
    </CrossSiteClickContext.Consumer>
  );
};

const trackContentUploader = (
  interactionMutationFunction: LogInteractionMutation,
  props: Props,
) => {
  const { children } = props;
  return (
    <ContentUploaderTrackingContext.Consumer>
      {contentUploaderContext => {
        const logOrDelayInteraction = (
          event: ?SyntheticEvent<*>,
          interactionType: InteractionType,
          additionalVariables?: {},
        ) => {
          const variables = additionalVariables || {};
          const contentUploaderData = {};
          contentUploaderData[interactionType] = {
            ...variables,
            ...contentUploaderContext,
          };

          const interactionData = {
            contentUploader: contentUploaderData,
          };
          logDataLaterOrNow(event, interactionData, interactionMutationFunction);
        };
        return children(logOrDelayInteraction);
      }}
    </ContentUploaderTrackingContext.Consumer>
  );
};

const trackGlobalNav = (interactionMutationFunction: LogInteractionMutation, props: Props) => {
  const { children } = props;
  return (
    <GlobalNavTrackingContext.Consumer>
      {globalNavTrackingContext => {
        const logOrDelayInteraction = (
          event: ?SyntheticEvent<*>,
          interactionType: InteractionType,
          additionalVariables?: {},
        ) => {
          const variables = additionalVariables || {};
          const globalNavData = {};
          globalNavData[interactionType] = {
            ...variables,
            ...globalNavTrackingContext,
          };
          const interactionData = {
            globalNav: globalNavData,
          };
          logDataLaterOrNow(event, interactionData, interactionMutationFunction);
        };
        return children(logOrDelayInteraction);
      }}
    </GlobalNavTrackingContext.Consumer>
  );
};

const trackRestaurants = (
  interactionMutationFunction: LogInteractionMutation,
  props: Props,
  namespace: string,
) => {
  const { children } = props;
  return (
    <RestaurantsTrackingContext.Consumer>
      {restaurantsTrackingContext => {
        const logOrDelayInteraction = (
          event: ?SyntheticEvent<*>,
          interactionType: InteractionType,
          additionalVariables?: {},
        ) => {
          const variables = additionalVariables || {};
          const restaurantsData = {};
          restaurantsData[interactionType] = {
            ...variables,
            ...restaurantsTrackingContext,
          };
          const namespaceData = {};
          namespaceData[namespace] = restaurantsData;
          logDataLaterOrNow(event, namespaceData, interactionMutationFunction);
        };
        return children(logOrDelayInteraction);
      }}
    </RestaurantsTrackingContext.Consumer>
  );
};

const trackOwnerNewsFeed = (interactionMutationFunction: LogInteractionMutation, props: Props) => {
  const { children } = props;
  return (
    <MixerImpressionContext.Consumer>
      {mixerImpressionId => (
        <FeedSectionIdContext.Consumer>
          {feedSectionId => (
            <ItemIdContext.Consumer>
              {itemId => {
                const logOrDelayInteraction = (
                  event: ?SyntheticEvent<*>,
                  interactionType: InteractionType,
                  additionalVariables?: {},
                ) => {
                  const variables = additionalVariables || {};
                  const data = {};
                  switch (interactionType) {
                    case 'feedClick':
                    case 'howToRespondClick':
                    case 'makeFavoriteReviewClick':
                    case 'reportReviewClick':
                    case 'respondDirectMessageClick':
                    case 'respondReviewClick':
                      data[interactionType] = {
                        feedSectionId,
                        itemId: itemId.itemId,
                      };
                      Object.assign(data[interactionType], variables);
                      break;
                    case 'listingPerformanceClick':
                    case 'adsPerformanceClick':
                      data[interactionType] = {
                        feedSectionId,
                      };
                      Object.assign(data[interactionType], variables);
                      break;
                    case 'ownersClick':
                    case 'managePhotosClick':
                    case 'restaurantReviewClick':
                    case 'managementCenterClick':
                    case 'adsManagerClick':
                      data[interactionType] = {};
                      Object.assign(data[interactionType], variables);
                      break;
                    case 'showMore':
                      data[interactionType] = {
                        mixerImpressionId,
                      };
                      Object.assign(data[interactionType], variables);
                      break;
                    default: {
                      Logging.error(`Unknown ownerNewsFeed interaction type: ${interactionType}`);
                    }
                  }

                  const interactionData = {
                    ownerNewsFeed: data,
                  };
                  logDataLaterOrNow(event, interactionData, interactionMutationFunction);
                };
                return children(logOrDelayInteraction);
              }}
            </ItemIdContext.Consumer>
          )}
        </FeedSectionIdContext.Consumer>
      )}
    </MixerImpressionContext.Consumer>
  );
};

const InteractionLogger = (props: Props) => {
  const [, logInteraction] = useMutation(LogInteractions);
  const { isBoosted, itemType, itemId } = React.useContext(BoostTrackingContext);
  React.useEffect(() => {
    if (!props.isReadonlyMode) {
      // If there are any interactions still waiting to be logged, log them whenever the component updates
      logDelayedInteractions(logInteraction);
    }
  });
  if (props.isReadonlyMode) {
    return props.children(() => {}); // no-op in read-only mode
  }
  if (isBoosted) {
    return trackBoost(logInteraction, props, itemType || '', itemId || '');
  }

  return (
    <TrackingNamespaceContext.Consumer>
      {namespace => {
        if (namespace === 'mixer') {
          return trackMixer(logInteraction, props);
        }
        if (namespace === 'home') {
          return trackHome(logInteraction, props);
        }
        if (namespace === 'profile') {
          return trackProfile(logInteraction, props);
        }
        if (namespace === 'onboarding') {
          return trackOnboarding(logInteraction, props);
        }
        if (namespace === 'inbox') {
          return trackInbox(logInteraction, props);
        }
        if (namespace === 'search') {
          return trackSearch(logInteraction, props);
        }
        if (namespace === 'ugcDetail') {
          return trackUgcDetail(logInteraction, props);
        }
        // for now it's just hotelReviewServlet but we will likely have more later
        if (namespace === 'hotelReviewServlet') {
          return trackHotels(logInteraction, props, namespace);
        }
        // These are for all Hotels list page user actions. Currently only for filters and (eventually) hotel cells.
        // These are used to replace GARecord based tracking. These should not be confused with the tracking namespaces
        // for Hotels with the string `Servlet` appended. Those are for social media feed tracking.
        if (
          namespace === 'hotels' ||
          namespace === 'smartDeals' ||
          namespace === 'hotelHighlight' ||
          namespace === 'hotelsNear' ||
          namespace === 'hotelsList' ||
          namespace === 'lastMinute'
        ) {
          return (
            <TrackHotelsListPage
              interactionMutationFunction={logInteraction}
              props={props}
              namespace={namespace}
            />
          );
        }
        if (namespace === 'flightsResults') {
          return trackFlightsResults(logInteraction, props, namespace);
        }
        if (namespace === 'flights') {
          return trackFlights(logInteraction, props, namespace);
        }
        if (namespace === 'globalNav') {
          return trackGlobalNav(logInteraction, props);
        }
        if (namespace === 'contentUploader') {
          return trackContentUploader(logInteraction, props);
        }
        if (namespace === 'ownerNewsFeed') {
          return trackOwnerNewsFeed(logInteraction, props);
        }
        if (namespace === 'restaurants') {
          return trackRestaurants(logInteraction, props, namespace);
        }
        return props.children(() => {});
      }}
    </TrackingNamespaceContext.Consumer>
  );
};

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  loggedInUserId: state.auth.loggedInUserId,
  isReadonlyMode: state.meta.readonlyMode,
}))(InteractionLogger);
